import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { AlertTriangle } from 'lucide-react';
import { RADIUS, TYPE, useTheme } from '../../theme';

/** Linjeikon från lucide-react i appens standard: 20 px, streck 2, ärver textfärgen. */
export function Icon({ as: C, size = 20, label, color }: { as: LucideIcon; size?: number; label?: string; color?: string }) {
  return <C size={size} strokeWidth={2} color={color} aria-hidden={label ? undefined : true} aria-label={label} role={label ? 'img' : undefined} style={{ flexShrink: 0 }} />;
}

/** Tomt läge: ikon i en rund ruta, rubrik, förklaring och gärna en knapp. */
export function EmptyState({ icon, title, sub, action }: { icon?: LucideIcon; title: React.ReactNode; sub?: React.ReactNode; action?: React.ReactNode }) {
  const T = useTheme();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '40px 20px', gap: 8 }}>
      {icon && (
        <div style={{ width: 56, height: 56, borderRadius: RADIUS.lg, background: T.accentSoft, color: T.accentText, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 6 }}>
          <Icon as={icon} size={26} />
        </div>
      )}
      <div style={{ ...TYPE.title, color: T.text }}>{title}</div>
      {sub && <div style={{ ...TYPE.small, fontSize: 14, color: T.textMuted, maxWidth: 360, lineHeight: 1.5 }}>{sub}</div>}
      {action && <div style={{ marginTop: 10 }}>{action}</div>}
    </div>
  );
}

/** Spinner med text, för laddningslägen. */
export function Loader({ text = 'Laddar…', inline }: { text?: string; inline?: boolean }) {
  const T = useTheme();
  return (
    <div role="status" style={{ display: 'flex', flexDirection: inline ? 'row' : 'column', alignItems: 'center', justifyContent: 'center', gap: inline ? 10 : 14, padding: inline ? 8 : '48px 20px' }}>
      <span aria-hidden style={{ width: inline ? 18 : 28, height: inline ? 18 : 28, border: `3px solid ${T.border}`, borderTopColor: T.amber, borderRadius: '50%', animation: 'spin .8s linear infinite' }} />
      <span style={{ ...TYPE.small, color: T.textMuted }}>{text}</span>
    </div>
  );
}

/** Fel som ska synas: ruta i dangerSoft med text och valfri knapp ("Försök igen"). */
export function ErrorState({ title = 'Något gick fel', message, action }: { title?: React.ReactNode; message?: React.ReactNode; action?: React.ReactNode }) {
  const T = useTheme();
  return (
    <div role="alert" style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: 16, borderRadius: RADIUS.md, background: T.dangerSoft, color: T.danger }}>
      <Icon as={AlertTriangle} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 700 }}>{title}</div>
        {message && <div style={{ fontSize: 14, marginTop: 2, color: T.text }}>{message}</div>}
        {action && <div style={{ marginTop: 10 }}>{action}</div>}
      </div>
    </div>
  );
}
